/**
 * Auth Guard Component
 * Protects routes that require authentication
 *
 * Features:
 * - Redirects unauthenticated users to login
 * - Shows loading state while checking auth
 * - Supports role-based access control
 * - Preserves intended destination for redirect after login
 */

import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./useAuth";

// ============================================
// Types
// ============================================

interface AuthGuardProps {
  children: ReactNode;
  /** Roles allowed to access this route. If empty/undefined, any authenticated user can access. */
  allowedRoles?: string[];
  /** Route to redirect to when not authenticated */
  loginPath?: string;
  /** Route to redirect to when user doesn't have required role */
  unauthorizedPath?: string;
  /** Custom loading component */
  loadingComponent?: ReactNode;
}

// ============================================
// Default Loading Component
// ============================================

function DefaultLoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-600 text-sm">Verifying authentication...</p>
      </div>
    </div>
  );
}

// ============================================
// AuthGuard Component
// ============================================

export function AuthGuard({
  children,
  allowedRoles,
  loginPath = "/login",
  unauthorizedPath = "/unauthorized",
  loadingComponent,
}: AuthGuardProps) {
  const { isLoading, isAuthenticated, user, profile } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return <>{loadingComponent || <DefaultLoadingSpinner />}</>;
  }

  // Not authenticated - redirect to login with return URL
  if (!isAuthenticated || !user) {
    console.log("[AuthGuard] Not authenticated, redirecting to login");
    return (
      <Navigate
        to={loginPath}
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // Check role-based access if roles are specified
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = profile?.role;

    if (!userRole || !allowedRoles.includes(userRole)) {
      console.log(
        "[AuthGuard] User role not authorized:",
        userRole,
        "Required:",
        allowedRoles,
      );
      return <Navigate to={unauthorizedPath} replace />;
    }
  }

  // All checks passed, render children
  return <>{children}</>;
}

// ============================================
// Convenience Wrappers
// ============================================

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Simple wrapper that only requires authentication (no role check)
 */
export function RequireAuth({ children }: RequireAuthProps) {
  return <AuthGuard>{children}</AuthGuard>;
}

interface RequireRoleProps {
  children: ReactNode;
  roles: string[];
}

/**
 * Wrapper that requires specific role(s)
 */
export function RequireRole({ children, roles }: RequireRoleProps) {
  return <AuthGuard allowedRoles={roles}>{children}</AuthGuard>;
}

/**
 * Wrapper that requires System Administrator role
 */
export function RequireAdmin({ children }: RequireAuthProps) {
  return (
    <AuthGuard allowedRoles={["System Administrator"]}>{children}</AuthGuard>
  );
}

// ============================================
// Guest Guard (opposite of AuthGuard)
// ============================================

interface GuestGuardProps {
  children: ReactNode;
  /** Route to redirect authenticated users to */
  redirectTo?: string;
  /** Custom loading component */
  loadingComponent?: ReactNode;
}

/**
 * Protects routes that should only be accessible to non-authenticated users
 * (e.g., login, registration pages)
 */
export function GuestGuard({
  children,
  redirectTo = "/admin/dashboard",
  loadingComponent,
}: GuestGuardProps) {
  const { isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return <>{loadingComponent || <DefaultLoadingSpinner />}</>;
  }

  // Authenticated - redirect to intended destination or default
  if (isAuthenticated) {
    // Check if there's a saved destination from AuthGuard redirect
    const from = (location.state as { from?: string })?.from || redirectTo;
    console.log("[GuestGuard] Already authenticated, redirecting to:", from);
    return <Navigate to={from} replace />;
  }

  // Not authenticated, render children (login page, etc.)
  return <>{children}</>;
}
